import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import { Security } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { oktaAuth } from './config/okta-config';
import { AuthProvider } from '@/common/contexts/auth-provider';
import { useAuthentication } from '@/common-hooks/use-authentication';
import { DashboardProvider } from '@/common/contexts/dashboard-provider';
import Layout from '@/common-components/main-layout/layout';
import ProtectedRoute from '@/common-components/protected-route';
import Dashboard from './pages/dashboard';
import Users from './pages/users';
import Roles from './pages/roles';
import Audit from './pages/audit';
import Login from './pages/login';
import LoginCallback from './pages/login-callback';
import SearchDetail from './pages/search-detail';
import TransactionLookup from './pages/log-oracle/transaction-lookup';
import TransactionDetail from './pages/log-oracle/transaction-detail';
import LogOracleJobs from './pages/log-oracle/jobs';
import AIChat from './pages/log-oracle/ai-chat';
import AIKB from './pages/log-oracle/ai-kb';
import UserGuide from './pages/user-guide';
import { PERMISSIONS } from '@/common-constants/permissions';
import { getMUIXLicense } from '@/common-utilities';
import { Box, Skeleton, Stack } from '@mui/material';
import { theme } from '@/common/styles/theme/theme';

// Set MUI X License
const muiLicense = getMUIXLicense();
if (muiLicense) {
  LicenseInfo.setLicenseKey(muiLicense);
}

const LoadingSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={60} />
        <Skeleton variant="rectangular" height={200} />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={60} />
        ))}
      </Stack>
    </Box>
  </Box>
);

// App Routes component
function AppRoutes() {
  const { isAuthenticated, loading } = useAuthentication();
  const location = useLocation();

  return (
    <Routes>
      {/* Public routes — always rendered, never blocked by auth loading */}
      <Route path="/login" element={<Login />} />
      <Route path="/login/callback" element={<LoginCallback />} />

      {/* Protected routes — show skeleton while Okta resolves, then auth-gate */}
      <Route
        path="/*"
        element={
          loading ? (
            <LoadingSkeleton />
          ) : isAuthenticated ? (
            <Layout>
              <Routes>
                <Route path="/" element={<ProtectedRoute permission={PERMISSIONS.READ}><Dashboard /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute permission={PERMISSIONS.USER_MANAGEMENT}><Users /></ProtectedRoute>} />
                <Route path="/roles" element={<ProtectedRoute permission={PERMISSIONS.USER_MANAGEMENT}><Roles /></ProtectedRoute>} />
                <Route path="/audit" element={<ProtectedRoute permission={PERMISSIONS.AUDIT_LOGS}><Audit /></ProtectedRoute>} />
                <Route path="/search/:type/:id" element={<ProtectedRoute permission={PERMISSIONS.READ}><SearchDetail /></ProtectedRoute>} />
                <Route path="/log-oracle" element={<Navigate to="/log-oracle/transaction-lookup" replace />} />
                <Route path="/log-oracle/transaction-lookup" element={<ProtectedRoute permission={PERMISSIONS.TRANSACTIONS_IN_LOG_ORACLE}><TransactionLookup /></ProtectedRoute>} />
                <Route path="/log-oracle/transaction-lookup/:transactionId" element={<ProtectedRoute permission={PERMISSIONS.TRANSACTIONS_IN_LOG_ORACLE}><TransactionDetail /></ProtectedRoute>} />
                <Route path="/log-oracle/jobs" element={<ProtectedRoute permission={PERMISSIONS.JOBS_IN_LOG_ORACLE}><LogOracleJobs /></ProtectedRoute>} />
                <Route path="/log-oracle/ai-chat" element={<ProtectedRoute permission={PERMISSIONS.AI_CHAT_IN_LOG_ORACLE}><AIChat /></ProtectedRoute>} />
                <Route path="/log-oracle/ai-kb" element={<ProtectedRoute permission={PERMISSIONS.R_KB_IN_LOG_ORACLE}><AIKB /></ProtectedRoute>} />
                <Route path="/log-oracle/sherlock-chat" element={<Navigate to="/log-oracle/ai-chat" replace />} />
                <Route path="/log-oracle/sherlock-kb" element={<Navigate to="/log-oracle/ai-kb" replace />} />
                <Route path="/user-guide" element={<UserGuide />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          ) : (
            <Navigate to="/login" replace state={{ from: location }} />
          )
        }
      />
    </Routes>
  );
}

function App() {
  const restoreOriginalUri = async (_oktaAuth: unknown, originalUri: string) => {
    // Restore the original URI after login
    const relativeUri = toRelativeUrl(originalUri || '/', window.location.origin);
    window.location.replace(relativeUri);
  };

  if (!oktaAuth) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ p: 3 }}>
          <div>OAuth configuration is missing. Please configure your environment variables.</div>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <AuthProvider>
          <DashboardProvider>
            <AppRoutes />
          </DashboardProvider>
        </AuthProvider>
      </Security>
    </ThemeProvider>
  );
}

function AppWithRouter() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

export default AppWithRouter;
